import { mdiAutoFix, mdiCommentTextOutline, mdiLink, mdiPound, mdiTextBoxCheckOutline } from '@mdi/js'
import Icon from '@mdi/react'
import Button from '@mui/material/Button'
import { ARTICLE_TYPE } from 'interfaces'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useMatch, useNavigate } from 'react-router'
import { composerContentItemSelector, composerLinkSelector } from 'services/compose/selectors'
import { AIConversation, AIConversationKind } from 'services/compose/suggest/net'
import { openComposerContentDialog } from 'services/ui'
import { AIConversationsList } from '../AIConversationsList'
import Credits from '../Credits'
import ArticleCredits from '../Credits/ArticleCredits'
import SavedTexts from '../SavedTexts'
import { HashtagsGenerator } from './HashtagsGenerator'
import styles from './SuggestedTexts.pcss'

type SuggestedTextsView = 'texts' | 'credits' | 'link' | 'hashtags' | 'writer' | 'conversations'

export function SuggestedTexts({ className }: { className?: string }) {
  const dispatch = useDispatch()
  const match = useMatch('/composer/:view')
  const location = useLocation()
  const navigate = useNavigate()
  const content = useSelector(composerContentItemSelector)
  const link = useSelector(composerLinkSelector)
  const withCredits = useMemo(() => Boolean(content), [content])
  const withLink = useMemo(() => content?.type === ARTICLE_TYPE || Boolean(link), [content, link])
  const [activeTab, setActiveTab] = useState<SuggestedTextsView>('texts')

  const switchTab = useCallback((tab: SuggestedTextsView) => () => {
    setActiveTab(tab)
    if (tab === 'writer') {
      navigate('/composer/ai', { state: { ...location.state, opener: '/composer' } })
    }
  }, [navigate, location.state])

  const handleConversationSelect = useCallback((conversation: AIConversation) => {
    dispatch(openComposerContentDialog({ tab: 'generate', conversation }))
  }, [dispatch])

  const handleNewChat = useCallback((kind: AIConversationKind) => {
    dispatch(openComposerContentDialog({ tab: 'generate', kind }))
  }, [dispatch])

  useEffect(() => {
    if (match?.params?.view === 'ai' && activeTab !== 'writer') {
      switchTab('writer').call(null)
    }
  }, [match?.params?.view, switchTab, activeTab])

  useEffect(() => {
    setActiveTab(location.pathname === '/composer/ai' ? 'writer' : 'texts')
  }, [location.pathname])

  useEffect(() => {
    if (!withCredits && activeTab === 'credits') {
      setActiveTab('texts')
    }

    if (!withLink && activeTab === 'link') {
      setActiveTab('texts')
    }
  }, [withCredits, withLink, activeTab])

  useEffect(() => {
    if (link) {
      setActiveTab('link')
    }
  }, [link])

  return (
    <div className={`${styles.container} ${className || ''}`} data-testid="suggested-texts">
      <div className={styles.tabs}>
        <Button
          variant="text"
          className={`${styles.tab} ${activeTab === 'conversations' ? styles.active : ''}`}
          startIcon={<Icon path={mdiAutoFix} size="16px" />}
          onClick={switchTab('conversations')}
        >
          AI
        </Button>
        {/* <Button
          className={`${styles.tab} ${activeTab === 'writer' ? styles.active : ''}`}
          onClick={switchTab('writer')}
          startIcon={<Icon path={mdiAutoFix} size="16px" />}
        >
          <FormattedMessage id="composer.content-actions.ai-writer" />
        </Button> */}
        <Button
          className={`${styles.tab} ${activeTab === 'texts' ? styles.active : ''}`}
          onClick={switchTab('texts')}
          startIcon={<Icon path={mdiTextBoxCheckOutline} size="16px" />}
        >
          <FormattedMessage id="composer.content-actions.saved-texts" />
        </Button>
        <Button
          variant="text"
          className={`${styles.tab} ${activeTab === 'hashtags' ? styles.active : ''}`}
          startIcon={<Icon path={mdiPound} size="16px" />}
          onClick={switchTab('hashtags')}
        >
          Hashtags
        </Button>
        {withLink && (
          <Button
            variant="text"
            className={`${styles.tab} ${activeTab === 'link' ? styles.active : ''}`}
            startIcon={<Icon path={mdiLink} size="16px" />}
            onClick={switchTab('link')}
          >
            <FormattedMessage id="composer.content-actions.article-info" />
          </Button>
        )}
        {withCredits && (
          <Button
            variant="text"
            className={`${styles.tab} ${activeTab === 'credits' ? styles.active : ''}`}
            startIcon={<Icon path={mdiCommentTextOutline} size="16px" />}
            onClick={switchTab('credits')}
          >
            <FormattedMessage id="composer.content-actions.credits" />
          </Button>
        )}
      </div>
      <div className={styles.content}>
        {activeTab === 'texts' && (
          <SavedTexts />
        )}
        {activeTab === 'link' && (
          <ArticleCredits />
        )}
        {activeTab === 'credits' && (
          <Credits />
        )}
        {activeTab === 'hashtags' && (
          <HashtagsGenerator />
        )}
        {activeTab === 'conversations' && (
          <AIConversationsList onConversationSelect={handleConversationSelect} onNewChat={handleNewChat} />
        )}
      </div>
    </div>
  )
}
